import React from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Phone, Mail, Globe, ArrowUpRight } from 'lucide-react';
import { COMPANY, SERVICES } from '../mock/data';
import { useT } from '../i18n/LanguageContext';
import { SERVICE_TRANSLATIONS } from '../i18n/translations';

const Footer = () => {
  const { t, lang } = useT();
  return (
    <footer className="relative bg-brand-blueDark text-white/85 overflow-hidden" data-testid="site-footer">
      {/* Ambient blobs */}
      <div className="absolute -top-40 -right-40 w-[600px] h-[600px] rounded-full bg-brand-blueLight/10 blur-3xl pointer-events-none"></div>
      <div className="absolute -bottom-40 -left-40 w-[600px] h-[600px] rounded-full bg-brand-blue/20 blur-3xl pointer-events-none"></div>

      {/* Shimmer top border */}
      <div className="shimmer-bar h-[3px] w-full"></div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand column */}
          <div>
            <div className="flex items-center gap-4 mb-6">
              <div className="w-24 h-24 md:w-28 md:h-28 rounded-md bg-white p-2.5 flex items-center justify-center flex-shrink-0">
                <img src={COMPANY.logo} alt="Logo" className="w-full h-full object-contain" />
              </div>
              <div>
                <div className="font-display text-2xl md:text-3xl text-white leading-tight">Frachigers</div>
                <div className="text-[10px] uppercase tracking-[0.3em] text-brand-blueSky">Handwerk · Seelisberg</div>
              </div>
            </div>
            <p className="text-sm leading-relaxed text-white/70 italic mb-6">«Zufriedene Kunden sind unser Antrieb und unsere Motivation.»</p>
            <div className="inline-flex items-center gap-2 text-brand-blueSky text-xs uppercase tracking-widest font-bold">
              <span className="h-px w-8 bg-brand-blueLight"></span>
              Armin Achermann
            </div>
          </div>

          {/* Services column */}
          <div>
            <h4 className="text-white uppercase tracking-[0.25em] text-xs mb-6 font-bold">{t('footer.services')}</h4>
            <ul className="space-y-3 text-sm">
              {SERVICES.map((s) => (
                <li key={s.id}>
                  <Link
                    to={`/dienstleistungen/${s.slug}`}
                    className="group inline-flex items-center gap-2 hover:text-white text-white/70 transition-colors"
                    data-testid={`footer-service-${s.slug}`}
                  >
                    <span className="w-1.5 h-1.5 rounded-full bg-brand-blueLight group-hover:bg-white transition-colors"></span>
                    {SERVICE_TRANSLATIONS[s.id]?.[lang]?.short}
                    <ArrowUpRight className="w-3.5 h-3.5 opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all" />
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Navigation column */}
          <div>
            <h4 className="text-white uppercase tracking-[0.25em] text-xs mb-6 font-bold">{t('footer.navigation')}</h4>
            <ul className="space-y-3 text-sm">
              <li><Link to="/" className="text-white/70 hover:text-white transition-colors" data-testid="footer-nav-home">{t('nav.home')}</Link></li>
              <li><Link to="/referenzen" className="text-white/70 hover:text-white transition-colors" data-testid="footer-nav-references">{t('nav.references')}</Link></li>
              <li><Link to="/team" className="text-white/70 hover:text-white transition-colors" data-testid="footer-nav-team">{t('nav.team')}</Link></li>
              <li><Link to="/ueber-uns" className="text-white/70 hover:text-white transition-colors" data-testid="footer-nav-about">{t('nav.about')}</Link></li>
              <li><Link to="/kontakt" className="text-white/70 hover:text-white transition-colors" data-testid="footer-nav-contact">{t('nav.contact')}</Link></li>
            </ul>
          </div>

          {/* Contact column */}
          <div>
            <h4 className="text-white uppercase tracking-[0.25em] text-xs mb-6 font-bold">{t('footer.contact')}</h4>
            <ul className="space-y-4 text-sm">
              <li className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-md bg-white/10 border border-white/20 flex items-center justify-center flex-shrink-0">
                  <MapPin className="w-4 h-4 text-brand-blueSky" />
                </div>
                <span className="text-white/80 pt-1">{COMPANY.address.street}<br />{COMPANY.address.city}</span>
              </li>
              <li className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-md bg-white/10 border border-white/20 flex items-center justify-center flex-shrink-0">
                  <Phone className="w-4 h-4 text-brand-blueSky" />
                </div>
                <a href={`tel:${COMPANY.phoneInternational}`} className="text-white font-bold hover:text-brand-blueSky transition-colors pt-1" data-testid="footer-phone">
                  {COMPANY.phone}
                </a>
              </li>
              <li className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-md bg-white/10 border border-white/20 flex items-center justify-center flex-shrink-0">
                  <Mail className="w-4 h-4 text-brand-blueSky" />
                </div>
                <a href={`mailto:${COMPANY.email}`} className="text-white/80 hover:text-white transition-colors break-all pt-1" data-testid="footer-email">
                  {COMPANY.email}
                </a>
              </li>
              <li className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-md bg-white/10 border border-white/20 flex items-center justify-center flex-shrink-0">
                  <Globe className="w-4 h-4 text-brand-blueSky" />
                </div>
                <span className="text-white/70 pt-1">{COMPANY.website}</span>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-16 pt-6 border-t border-white/15 flex flex-col md:flex-row justify-between items-center gap-4 text-xs text-white/50">
          <div className="flex flex-col md:flex-row md:items-center gap-2 md:gap-6">
            <div>© {new Date().getFullYear()} {COMPANY.name}. {t('footer.rights')}</div>
            <div className="flex items-center gap-4">
              <Link to="/impressum" className="hover:text-white transition-colors" data-testid="footer-impressum">Impressum</Link>
              <span className="text-white/20">·</span>
              <Link to="/datenschutz" className="hover:text-white transition-colors" data-testid="footer-datenschutz">Datenschutz</Link>
            </div>
          </div>
          <div className="font-display text-brand-blueSky text-base italic">AA+ Qualität · Zuverlässig · Persönlich</div>
        </div>

        {/* Alpen Pro Agency credit — attention-grabbing */}
        <div className="mt-8 pt-6 border-t border-white/10 text-center">
          <a
            href="https://alpenpro.agency/"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 group"
            data-testid="footer-alpenpro"
          >
            <span className="text-[10px] uppercase tracking-[0.3em] text-white/50 group-hover:text-white/70 transition-colors">Webseite erstellt von</span>
            <span className="alpen-pulse text-sm md:text-base font-bold uppercase tracking-[0.25em] text-brand-blueSky group-hover:text-white transition-colors">
              Alpen Pro Agency
            </span>
            <span className="w-1.5 h-1.5 rounded-full bg-brand-blueSky animate-ping"></span>
          </a>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
